/**
 * BulkEditModal - Modal for bulk editing multiple items at once
 *
 * Features:
 * - Field selection with content type-specific options
 * - Categories/tags add/remove mode
 * - Dangerous operation warnings
 * - Summary before save with confirmation
 * - Progress tracking during save
 * - Results display with error handling
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useTaxonomies, type TaxonomyTerm } from '@/hooks/useTaxonomies';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  contentType: 'posts' | 'pages' | 'media' | 'users';
  selectedItems: Array<{
    id: string;
    wp_id: number;
    title: string;
  }>;
  onSuccess?: () => void;
}

type FieldKey =
  | 'status'
  | 'author'
  | 'categories'
  | 'tags'
  | 'comment_status'
  | 'ping_status'
  | 'date';

interface FieldConfig {
  key: FieldKey;
  label: string;
  type: 'select' | 'multiselect' | 'date' | 'number';
  description?: string;
  warning?: 'danger' | 'caution';
  warningMessage?: string;
  contentTypes: Array<'posts' | 'pages' | 'media' | 'users'>;
  options?: Array<{ value: string; label: string }>;
  taxonomyMode?: boolean; // For categories/tags: add or remove mode
  editable?: boolean; // Users role is display-only
}

interface BulkEditPayload {
  wpIds: number[];
  fields: Record<string, unknown>;
}

interface BulkEditResult {
  success: boolean;
  updated: number;
  failed: number;
  errors: Array<{
    wpId: number;
    error: string;
  }>;
}

type ModalStep = 'edit' | 'confirm' | 'progress' | 'results';

// -----------------------------------------------------------------------------
// Field Configuration
// -----------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: 'publish', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending Review' },
  { value: 'private', label: 'Private' },
  { value: 'trash', label: 'Trash' },
];

const COMMENT_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

const PING_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

const FIELD_CONFIGS: FieldConfig[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    description: 'Change the publication status',
    contentTypes: ['posts', 'pages', 'media'],
    options: STATUS_OPTIONS,
  },
  {
    key: 'author',
    label: 'Author',
    type: 'number',
    description: 'Change the author (by user ID)',
    warning: 'caution',
    warningMessage: 'Changing authorship affects attribution and may impact SEO.',
    contentTypes: ['posts', 'pages', 'media'],
  },
  {
    key: 'categories',
    label: 'Categories',
    type: 'multiselect',
    description: 'Add or remove categories',
    contentTypes: ['posts'],
    taxonomyMode: true,
  },
  {
    key: 'tags',
    label: 'Tags',
    type: 'multiselect',
    description: 'Add or remove tags',
    contentTypes: ['posts'],
    taxonomyMode: true,
  },
  {
    key: 'comment_status',
    label: 'Comments',
    type: 'select',
    description: 'Allow or disallow comments',
    contentTypes: ['posts', 'pages'],
    options: COMMENT_STATUS_OPTIONS,
  },
  {
    key: 'ping_status',
    label: 'Pingbacks',
    type: 'select',
    description: 'Allow or disallow pingbacks/trackbacks',
    contentTypes: ['posts', 'pages'],
    options: PING_STATUS_OPTIONS,
  },
  {
    key: 'date',
    label: 'Publish Date',
    type: 'date',
    description: 'Change the publish date',
    contentTypes: ['posts', 'pages'],
  },
];

// -----------------------------------------------------------------------------
// Helper Components
// -----------------------------------------------------------------------------

function WarningBanner({
  type,
  message,
}: {
  type: 'danger' | 'caution';
  message: string;
}) {
  const isDanger = type === 'danger';
  return (
    <div
      className={cn(
        'flex items-start gap-2 px-3 py-2 rounded-lg text-sm',
        isDanger
          ? 'bg-red-50 border border-red-200 text-red-700'
          : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
      )}
    >
      <svg
        className={cn('w-4 h-4 mt-0.5 flex-shrink-0', isDanger ? 'text-red-500' : 'text-yellow-500')}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <span>{message}</span>
    </div>
  );
}

function TaxonomyMultiSelect({
  terms,
  selectedIds,
  onChange,
  isLoading,
  placeholder,
}: {
  terms: TaxonomyTerm[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  isLoading: boolean;
  placeholder: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTerms = useMemo(() => {
    if (!search) return terms;
    const lower = search.toLowerCase();
    return terms.filter(t => t.label.toLowerCase().includes(lower));
  }, [terms, search]);

  const selectedTerms = useMemo(
    () => terms.filter(t => selectedIds.includes(Number(t.value))),
    [terms, selectedIds]
  );

  const toggleTerm = (termId: number) => {
    if (selectedIds.includes(termId)) {
      onChange(selectedIds.filter(id => id !== termId));
    } else {
      onChange([...selectedIds, termId]);
    }
  };

  if (isLoading) {
    return (
      <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm">
        Loading...
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between gap-2 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <div className="flex-1 flex flex-wrap gap-1 min-h-[1.25rem]">
          {selectedTerms.length === 0 ? (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          ) : (
            selectedTerms.map(term => (
              <span
                key={term.value}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
              >
                {term.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTerm(Number(term.value));
                  }}
                  className="hover:text-blue-600"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))
          )}
        </div>
        <svg
          className={cn('w-4 h-4 text-gray-400 transition-transform', isOpen && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredTerms.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No items found</div>
            ) : (
              filteredTerms.map(term => (
                <label
                  key={term.value}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(Number(term.value))}
                    onChange={() => toggleTerm(Number(term.value))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 flex-1">{term.label}</span>
                  <span className="text-xs text-gray-400">({term.count})</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const percent = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">Updating item {current} of {total}...</span>
        <span className="font-medium text-gray-900">{percent}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export function BulkEditModal({
  isOpen,
  onClose,
  siteId,
  contentType,
  selectedItems,
  onSuccess,
}: BulkEditModalProps) {
  const queryClient = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);

  // UI state
  const [step, setStep] = useState<ModalStep>('edit');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [progress, setProgress] = useState(0);

  // Field selection state
  const [enabledFields, setEnabledFields] = useState<Set<FieldKey>>(new Set());
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [taxonomyModes, setTaxonomyModes] = useState<Record<string, 'add' | 'remove'>>({
    categories: 'add',
    tags: 'add',
  });

  // Fetch taxonomies for categories/tags
  const { categories, tags, isLoading: taxonomiesLoading } = useTaxonomies(siteId);

  // Available fields for this content type
  const availableFields = useMemo(
    () => FIELD_CONFIGS.filter(f => f.contentTypes.includes(contentType) && f.editable !== false),
    [contentType]
  );

  // Check for dangerous fields
  const hasDangerousFields = useMemo(() => {
    if (!enabledFields.has('status')) return false;
    return fieldValues.status === 'trash';
  }, [enabledFields, fieldValues.status]);

  const hasCautionFields = useMemo(() => {
    return enabledFields.has('author');
  }, [enabledFields]);

  // Bulk edit mutation
  const bulkEditMutation = useMutation({
    mutationFn: async (payload: BulkEditPayload) => {
      // Simulate progress for better UX
      const totalItems = payload.wpIds.length;
      for (let i = 0; i < totalItems; i++) {
        setProgress(i + 1);
        // Small delay for visual feedback (actual API handles all at once)
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      return api.post<BulkEditResult>(
        `/api/v1/sites/${siteId}/content/${contentType}/bulk-edit`,
        payload
      );
    },
    onSuccess: (result) => {
      setStep('results');
      queryClient.invalidateQueries({ queryKey: ['site-content', siteId] });
      if (result.success && onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      setStep('results');
    },
  });

  // Build the final payload
  const buildPayload = useCallback((): BulkEditPayload => {
    const fields: Record<string, unknown> = {};

    enabledFields.forEach(key => {
      const value = fieldValues[key];
      if (value === undefined || value === null || value === '') return;

      if (key === 'categories' || key === 'tags') {
        // Include mode for taxonomy fields
        fields[key] = {
          mode: taxonomyModes[key],
          ids: value,
        };
      } else {
        fields[key] = value;
      }
    });

    return {
      wpIds: selectedItems.map(item => item.wp_id),
      fields,
    };
  }, [enabledFields, fieldValues, taxonomyModes, selectedItems]);

  // Handle field toggle
  const toggleField = (key: FieldKey) => {
    const newEnabled = new Set(enabledFields);
    if (newEnabled.has(key)) {
      newEnabled.delete(key);
      // Clear the value when disabling
      setFieldValues(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      newEnabled.add(key);
    }
    setEnabledFields(newEnabled);
  };

  // Handle field value change
  const setFieldValue = (key: FieldKey, value: unknown) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
  };

  // Handle taxonomy mode change
  const setTaxonomyMode = (key: string, mode: 'add' | 'remove') => {
    setTaxonomyModes(prev => ({ ...prev, [key]: mode }));
  };

  // Get summary of changes
  const getSummary = useCallback(() => {
    const changes: Array<{ field: string; value: string; warning?: 'danger' | 'caution' }> = [];

    enabledFields.forEach(key => {
      const config = FIELD_CONFIGS.find(f => f.key === key);
      if (!config) return;

      const value = fieldValues[key];
      if (value === undefined || value === null || value === '') return;

      let displayValue = '';
      if (key === 'categories' || key === 'tags') {
        const ids = value as number[];
        const terms = key === 'categories' ? categories : tags;
        const names = terms.filter(t => ids.includes(Number(t.value))).map(t => t.label);
        displayValue = `${taxonomyModes[key] === 'add' ? 'Add' : 'Remove'}: ${names.join(', ')}`;
      } else if (config.options) {
        const option = config.options.find(o => o.value === value);
        displayValue = option?.label || String(value);
      } else if (key === 'date') {
        displayValue = new Date(value as string).toLocaleDateString();
      } else {
        displayValue = String(value);
      }

      let warning: 'danger' | 'caution' | undefined;
      if (key === 'status' && value === 'trash') {
        warning = 'danger';
      } else if (config.warning) {
        warning = config.warning;
      }

      changes.push({
        field: config.label,
        value: displayValue,
        warning,
      });
    });

    return changes;
  }, [enabledFields, fieldValues, taxonomyModes, categories, tags]);

  // Reset state when modal closes or opens
  useEffect(() => {
    if (isOpen) {
      setStep('edit');
      setEnabledFields(new Set());
      setFieldValues({});
      setTaxonomyModes({ categories: 'add', tags: 'add' });
      setConfirmChecked(false);
      setProgress(0);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && step !== 'progress') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, step]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const itemCount = selectedItems.length;
  const hasEnabledFields = enabledFields.size > 0;
  const needsConfirmation = hasDangerousFields || hasCautionFields;

  // Handle next step
  const handleNext = () => {
    if (step === 'edit') {
      if (needsConfirmation) {
        setStep('confirm');
      } else {
        setStep('progress');
        bulkEditMutation.mutate(buildPayload());
      }
    } else if (step === 'confirm') {
      setStep('progress');
      bulkEditMutation.mutate(buildPayload());
    }
  };

  // Handle back
  const handleBack = () => {
    if (step === 'confirm') {
      setStep('edit');
      setConfirmChecked(false);
    }
  };

  // Render field input based on type
  const renderFieldInput = (config: FieldConfig) => {
    const isEnabled = enabledFields.has(config.key);
    const value = fieldValues[config.key];

    return (
      <div
        key={config.key}
        className={cn(
          'border rounded-lg p-4 transition-colors',
          isEnabled ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200 bg-white'
        )}
      >
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={() => toggleField(config.key)}
            className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{config.label}</span>
              {config.warning === 'danger' && (
                <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                  Danger
                </span>
              )}
              {config.warning === 'caution' && (
                <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                  Caution
                </span>
              )}
            </div>
            {config.description && (
              <p className="text-sm text-gray-500 mt-0.5">{config.description}</p>
            )}

            {isEnabled && (
              <div className="mt-3 space-y-2">
                {/* Warning message */}
                {config.warningMessage && (
                  <WarningBanner type={config.warning || 'caution'} message={config.warningMessage} />
                )}

                {/* Taxonomy mode selector */}
                {config.taxonomyMode && (
                  <div className="flex items-center gap-4 mb-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`${config.key}-mode`}
                        checked={taxonomyModes[config.key] === 'add'}
                        onChange={() => setTaxonomyMode(config.key, 'add')}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Add to existing</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`${config.key}-mode`}
                        checked={taxonomyModes[config.key] === 'remove'}
                        onChange={() => setTaxonomyMode(config.key, 'remove')}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Remove from items</span>
                    </label>
                  </div>
                )}

                {/* Field input */}
                {config.type === 'select' && config.options && (
                  <select
                    value={(value as string) || ''}
                    onChange={(e) => setFieldValue(config.key, e.target.value)}
                    className={cn(
                      'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                      config.key === 'status' && value === 'trash'
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                    )}
                  >
                    <option value="">Select...</option>
                    {config.options.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}

                {config.type === 'number' && (
                  <input
                    type="number"
                    value={(value as number) || ''}
                    onChange={(e) => setFieldValue(config.key, e.target.value ? Number(e.target.value) : '')}
                    placeholder="Enter user ID..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )}

                {config.type === 'date' && (
                  <input
                    type="datetime-local"
                    value={(value as string) || ''}
                    onChange={(e) => setFieldValue(config.key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )}

                {config.type === 'multiselect' && config.key === 'categories' && (
                  <TaxonomyMultiSelect
                    terms={categories}
                    selectedIds={(value as number[]) || []}
                    onChange={(ids) => setFieldValue(config.key, ids)}
                    isLoading={taxonomiesLoading}
                    placeholder="Select categories..."
                  />
                )}

                {config.type === 'multiselect' && config.key === 'tags' && (
                  <TaxonomyMultiSelect
                    terms={tags}
                    selectedIds={(value as number[]) || []}
                    onChange={(ids) => setFieldValue(config.key, ids)}
                    isLoading={taxonomiesLoading}
                    placeholder="Select tags..."
                  />
                )}

                {/* Trash warning */}
                {config.key === 'status' && value === 'trash' && (
                  <WarningBanner
                    type="danger"
                    message={`Moving ${itemCount} item${itemCount !== 1 ? 's' : ''} to trash. They will need to be restored or permanently deleted from WordPress.`}
                  />
                )}
              </div>
            )}
          </div>
        </label>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={step !== 'progress' ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={cn(
          'relative w-full max-w-2xl max-h-[85vh] mx-4',
          'bg-white rounded-xl shadow-2xl',
          'flex flex-col overflow-hidden',
          'animate-in fade-in-0 zoom-in-95 duration-200'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {/* Icon */}
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                hasDangerousFields
                  ? 'bg-red-100'
                  : hasCautionFields
                  ? 'bg-yellow-100'
                  : 'bg-blue-100'
              )}
            >
              <svg
                className={cn(
                  'w-5 h-5',
                  hasDangerousFields
                    ? 'text-red-600'
                    : hasCautionFields
                    ? 'text-yellow-600'
                    : 'text-blue-600'
                )}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Bulk Edit {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </h2>
              <p className="text-sm text-gray-500 capitalize">{contentType}</p>
            </div>
          </div>
          {step !== 'progress' && (
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step: Edit */}
          {step === 'edit' && (
            <div className="space-y-4">
              {/* Important notice */}
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <svg
                  className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="text-sm text-amber-800">
                  <p className="font-medium">This is a powerful operation</p>
                  <p className="mt-1">
                    Select the fields you want to change. Only checked fields will be updated.
                    Changes apply to all {itemCount} selected items.
                  </p>
                </div>
              </div>

              {/* Field list */}
              <div className="space-y-3">
                {availableFields.map(config => renderFieldInput(config))}
              </div>

              {/* Users info */}
              {contentType === 'users' && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                  User bulk editing is limited for security reasons. Role changes require
                  individual editing.
                </div>
              )}

              {/* Selected items preview */}
              {selectedItems.length > 0 && selectedItems.length <= 10 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Items:</h3>
                  <ul className="text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                    {selectedItems.map(item => (
                      <li key={item.id} className="truncate">
                        <span className="text-gray-400 mr-2">#{item.wp_id}</span>
                        {item.title || '(No title)'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Step: Confirm */}
          {step === 'confirm' && (
            <div className="space-y-6">
              {/* Danger warning */}
              {hasDangerousFields && (
                <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800 font-medium">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    Dangerous Operation
                  </div>
                  <p className="text-sm text-red-700 mt-2">
                    You are about to move {itemCount} item{itemCount !== 1 ? 's' : ''} to the trash.
                    This action can be undone from WordPress, but please confirm you want to proceed.
                  </p>
                </div>
              )}

              {/* Summary */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Summary of Changes:</h3>
                <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
                  {getSummary().map((change, idx) => (
                    <div key={idx} className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm text-gray-600">{change.field}</span>
                      <span
                        className={cn(
                          'text-sm font-medium',
                          change.warning === 'danger'
                            ? 'text-red-700'
                            : change.warning === 'caution'
                            ? 'text-yellow-700'
                            : 'text-gray-900'
                        )}
                      >
                        {change.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Affected items count */}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <span>
                  This will update <strong>{itemCount}</strong> {contentType}
                </span>
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-start gap-3 p-4 bg-gray-100 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I understand that this will modify {itemCount} item{itemCount !== 1 ? 's' : ''} and
                  cannot be automatically undone. I have reviewed the changes above.
                </span>
              </label>
            </div>
          )}

          {/* Step: Progress */}
          {step === 'progress' && (
            <div className="space-y-6 py-8">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-blue-600 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900">Updating Items</h3>
                <p className="text-sm text-gray-500 mt-1">Please wait while we apply your changes...</p>
              </div>
              <ProgressBar current={progress} total={itemCount} />
            </div>
          )}

          {/* Step: Results */}
          {step === 'results' && (
            <div className="space-y-6">
              {bulkEditMutation.isSuccess ? (
                <>
                  {/* Success */}
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900">Update Complete</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Successfully updated {bulkEditMutation.data?.updated || itemCount} item
                      {(bulkEditMutation.data?.updated || itemCount) !== 1 ? 's' : ''}
                    </p>
                  </div>

                  {/* Failed items */}
                  {bulkEditMutation.data?.failed && bulkEditMutation.data.failed > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-red-800 mb-2">
                        Failed to update {bulkEditMutation.data.failed} item
                        {bulkEditMutation.data.failed !== 1 ? 's' : ''}:
                      </h4>
                      <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {bulkEditMutation.data.errors.map((err, idx) => (
                          <li key={idx}>
                            <span className="font-mono">#{err.wpId}</span>: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Error */}
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-gray-900">Update Failed</h3>
                    <p className="text-sm text-red-600 mt-1">
                      {bulkEditMutation.error instanceof Error
                        ? bulkEditMutation.error.message
                        : 'An unexpected error occurred'}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div>
            {step === 'confirm' && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {step === 'edit' && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNext}
                  disabled={!hasEnabledFields}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    hasEnabledFields
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  )}
                >
                  {needsConfirmation ? 'Review Changes' : 'Apply Changes'}
                </button>
              </>
            )}

            {step === 'confirm' && (
              <button
                onClick={handleNext}
                disabled={!confirmChecked}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  confirmChecked
                    ? hasDangerousFields
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                )}
              >
                Confirm & Apply
              </button>
            )}

            {step === 'results' && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkEditModal;
